import { FaroTableBulkActions } from "@components/common/faro-table/faro-table-bulk-actions";
import { MapListTwoToneIcon } from "@components/common/icons";
import { VirtualizedGrid } from "@components/virtualized/virtualized-grid";
import { WorldMapHandle } from "@components/world-map/world-map-types";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { ProjectArchivingState, SdbProject } from "@custom-types/project-types";
import { ArrowDownIcon, neutral, Text } from "@flat-ui";
import { ButtonBase } from "@mui/material";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { ProjectsMapInfoPage } from "@pages/projects/projects-map/projects-map-info-page";
import { ProjectsMapListItem } from "@pages/projects/projects-map/projects-map-list-item";
import { ProjectsMapSkeleton } from "@pages/projects/projects-map/projects-map-skeleton";
import { useProjectsMapBulkActions } from "@pages/projects/projects-map/use-projects-map-bulk-actions";
import { useCallback, useMemo, useState } from "react";

/** Estimated height in pixels of a list item, used as the initial virtualization estimate */
const LIST_ITEM_HEIGHT = 132;

/** Label for the section listing the projects currently visible on the map */
const VISIBLE_SECTION_LABEL = "Visible on map";

/** Label for the section listing the projects not visible on the map */
const OTHER_SECTION_LABEL = "Other projects";

/** Identifies each collapsible section of the list */
type SectionId = "visible" | "other";

/** A row rendered in the virtualized list: either a section header or a project. */
type ListRow =
  | {
      kind: "header";
      key: string;
      section: SectionId;
      label: string;
      count: number;
    }
  | { kind: "project"; key: string; project: SdbProject };

interface Props extends BaseCompanyIdProps {
  /** Projects currently visible inside the map viewport, already sorted */
  visibleProjects: SdbProject[];

  /** Projects not visible in the map viewport (including projects without a position), already sorted */
  otherProjects: SdbProject[];

  /** The currently selected project archiving state */
  projectArchivingState: ProjectArchivingState;

  /** Callback to navigate to a project position on the projects map */
  goTo?: WorldMapHandle["goTo"];

  /** Called when a project card hover state changes */
  onHoverChange?: (projectId: string | null) => void;

  /** IDs of the projects whose cards should show the highlighted style */
  highlightedProjectIds: ReadonlySet<string>;

  /** Whether a search query is currently active */
  hasSearchQuery?: boolean;

  /** The active sort criteria */
  sortBy: FaroCoreApiTypes.ProjectsSortBy;
}

/**
 * @returns A clickable header that toggles the collapsed state of a project section.
 */
function SectionHeader({
  label,
  count,
  isCollapsed,
  onToggle,
}: {
  /** The section title */
  label: string;
  /** The number of projects in the section */
  count: number;
  /** Whether the section is currently collapsed */
  isCollapsed: boolean;
  /** Toggles the collapsed state of the section */
  onToggle: () => void;
}): JSX.Element {
  return (
    <ButtonBase
      onClick={onToggle}
      aria-expanded={!isCollapsed}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 0.5,
        width: "100%",
        pt: 1,
        pb: 0.5,
        px: "8px",
      }}
    >
      <ArrowDownIcon
        sx={{
          width: "1rem",
          height: "1rem",
          color: neutral[600],
          transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
          transition: "transform 0.2s ease-in-out",
        }}
      />
      <Text variant="labelM" color={neutral[600]}>
        {label} (<var>{count}</var>)
      </Text>
    </ButtonBase>
  );
}

/**
 * @returns A virtualized list that groups projects into a collapsible "visible on the map"
 * section and a collapsible "other projects" section, each sorted by the same active sort criteria.
 */
export function ProjectsMapList({
  companyId,
  visibleProjects,
  otherProjects,
  projectArchivingState,
  goTo,
  onHoverChange,
  highlightedProjectIds,
  hasSearchQuery,
  sortBy,
}: Props): JSX.Element {
  const [collapsedSections, setCollapsedSections] = useState<
    ReadonlySet<SectionId>
  >(() => new Set());

  const toggleSection = useCallback((section: SectionId) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  }, []);

  // Combined list of all projects across both sections, used for bulk selection and export.
  const projects = useMemo(
    () => [...visibleProjects, ...otherProjects],
    [visibleProjects, otherProjects]
  );

  // Flat list of rows to virtualize. Each non-empty section contributes a header row,
  // followed by its project rows unless the section is collapsed.
  const rows = useMemo<ListRow[]>(() => {
    const result: ListRow[] = [];

    if (visibleProjects.length > 0) {
      result.push({
        kind: "header",
        key: "header-visible",
        section: "visible",
        label: VISIBLE_SECTION_LABEL,
        count: visibleProjects.length,
      });
      if (!collapsedSections.has("visible")) {
        for (const project of visibleProjects) {
          result.push({ kind: "project", key: project.id, project });
        }
      }
    }

    if (otherProjects.length > 0) {
      result.push({
        kind: "header",
        key: "header-other",
        section: "other",
        label: OTHER_SECTION_LABEL,
        count: otherProjects.length,
      });
      if (!collapsedSections.has("other")) {
        for (const project of otherProjects) {
          result.push({ kind: "project", key: project.id, project });
        }
      }
    }

    return result;
  }, [visibleProjects, otherProjects, collapsedSections]);

  const { bulkActions, selectedProjects } = useProjectsMapBulkActions({
    companyId,
    projects,
    projectArchivingState,
  });

  if (projects.length === 0) {
    return (
      <ProjectsMapInfoPage
        title={
          hasSearchQuery
            ? "No matching projects found"
            : `No ${projectArchivingState} projects`
        }
        source={MapListTwoToneIcon}
      />
    );
  }

  return (
    <VirtualizedGrid
      items={rows}
      isLoading={false}
      estimateSize={LIST_ITEM_HEIGHT}
      shouldMeasureRowHeight
      columnConfig={{
        xs: 1,
        sm: 1,
        md: 1,
        lg: 1,
        xl: 1,
        xxl: 1,
        xxxl: 1,
      }}
      shouldRenderFooterOutside={selectedProjects.length > 0}
      renderSkeletonItem={(index) => <ProjectsMapSkeleton key={index} />}
      renderItem={(row) => {
        if (row.kind === "header") {
          return (
            <SectionHeader
              key={row.key}
              label={row.label}
              count={row.count}
              isCollapsed={collapsedSections.has(row.section)}
              onToggle={() => toggleSection(row.section)}
            />
          );
        }

        return (
          <ProjectsMapListItem
            key={row.key}
            project={row.project}
            goTo={goTo}
            onHoverChange={onHoverChange}
            isHighlighted={highlightedProjectIds.has(row.project.id)}
            sortBy={sortBy}
          />
        );
      }}
      renderFooterAction={() => (
        <FaroTableBulkActions
          bulkActionsButtons={bulkActions}
          numberOfSelectedRows={selectedProjects.length}
          isLoading={false}
          allRowIds={projects.map((project) => project.id)}
          isSelectAllAvailable
        />
      )}
    />
  );
}
